import type { ReactNode } from 'react'
import { Action } from './Action'

/**
 * Previous / next over a paged list.
 *
 * Every list in the app had written this block itself — four copies of the
 * same flex row, the same two `Action`s, the same "Page m of n" — which is
 * three copies too many for a control this small, and is how three of them
 * came to carry a bug the fourth didn't (see `queryHref`'s `page` in the
 * callers' `go`).
 *
 * It renders **above and below** the rows. A pager only at the foot means
 * scrolling a full page of results to reach the control that replaces them,
 * and then scrolling back up to read what arrived; with one at the head, the
 * next page starts where your eye already is. `where` is only ever about
 * spacing — the head one has no top margin to add and the foot one does — so
 * the two are the same control, not two controls that agree.
 */
export interface PagerState {
  page: number
  totalPages: number
  totalCount?: number
}

export function Pager({
  pagination,
  onGo,
  where = 'foot',
  note,
}: {
  pagination: PagerState
  onGo: (page: number) => void
  where?: 'head' | 'foot'
  /** Anything to say beside the count — "84 projects", say. */
  note?: ReactNode
}) {
  const { page, totalPages } = pagination
  if (totalPages <= 1) return null

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flex: 'none',
      }}
    >
      <span style={{ font: '400 11px Manrope,sans-serif', color: 'var(--color-muted)' }}>
        Page {page} of {totalPages}
        {note ? <> · {note}</> : null}
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <Action
          quietTone="muted"
          disabled={page <= 1}
          onClick={() => onGo(page - 1)}
          aria-label={where === 'head' ? 'Previous page' : undefined}
        >
          Previous
        </Action>
        <Action
          quietTone="muted"
          disabled={page >= totalPages}
          onClick={() => onGo(page + 1)}
          aria-label={where === 'head' ? 'Next page' : undefined}
        >
          Next
        </Action>
      </div>
    </div>
  )
}
